<template>
  <div class="roles-page">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Roles</h1>
        <p>Manage administrator and platform roles.</p>
      </div>

      <button class="primary-btn" @click="openAddModal">
        <i class="bx bx-plus"></i>
        Add Role
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon blue">
          <i class="bx bx-shield"></i>
        </div>

        <div>
          <span>Total Roles</span>
          <strong>{{ roles.length }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green">
          <i class="bx bx-check-circle"></i>
        </div>

        <div>
          <span>Configured Roles</span>
          <strong>{{ configuredRoles }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon purple">
          <i class="bx bx-key"></i>
        </div>

        <div>
          <span>Role Types</span>
          <strong>{{ roles.length }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon orange">
          <i class="bx bx-calendar"></i>
        </div>

        <div>
          <span>This Month</span>
          <strong>{{ thisMonthCount }}</strong>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <div class="content-card">
      <!-- Toolbar -->
      <div class="toolbar">
        <div class="search-box">
          <i class="bx bx-search"></i>

          <input
            v-model="search"
            type="text"
            placeholder="Search roles..."
          />
        </div>

        <button class="reset-btn" @click="resetFilters">
          <i class="bx bx-reset"></i>
          Reset
        </button>
      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Role</th>
              <th>Meta Title</th>
              <th>Keywords</th>
              <th>Created</th>
              <th class="action-column">Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="role in paginatedRoles"
              :key="role.role_id"
            >
              <!-- Role -->
              <td>
                <div class="role-info">
                  <div class="role-icon">
                    <i class="bx bx-shield"></i>
                  </div>

                  <div>
                    <strong>{{ role.role_title }}</strong>

                    <small>
                      Role #{{ role.role_id }}
                    </small>
                  </div>
                </div>
              </td>

              <!-- Meta Title -->
              <td>
                <div class="meta-cell">
                  {{ role.role_meta_title || "—" }}
                </div>
              </td>

              <!-- Keywords -->
              <td>
                <div class="keyword-cell">
                  {{ role.role_keyword || "—" }}
                </div>
              </td>

              <!-- Created -->
              <td>
                <span class="date-text">
                  {{ formatDate(role.role_datetime) }}
                </span>
              </td>

              <!-- Actions -->
              <td>
                <div class="actions">
                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewRole(role)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editRole(role)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="deleteRole(role)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="paginatedRoles.length === 0">
              <td colspan="5" class="empty-state">
                <i class="bx bx-shield-x"></i>
                <h3>No roles found</h3>
                <p>Try changing your search.</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        class="pagination"
        v-if="totalPages > 1"
      >
        <div class="pagination-info">
          Showing
          <strong>{{ startItem }}</strong>
          -
          <strong>{{ endItem }}</strong>
          of
          <strong>{{ filteredRoles.length }}</strong>
        </div>

        <div class="pagination-buttons">
          <button
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
            @click="currentPage = page"
          >
            {{ page }}
          </button>

          <button
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            <i class="bx bx-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeModal"
    >
      <div class="modal">
        <div class="modal-header">
          <div>
            <h2>
              {{ isEditing ? "Edit Role" : "Add Role" }}
            </h2>

            <p>
              {{
                isEditing
                  ? "Update role information."
                  : "Create a new platform role."
              }}
            </p>
          </div>

          <button
            class="close-btn"
            @click="closeModal"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <!-- Role Title -->
            <div class="form-group full">
              <label>
                Role Title
                <span>*</span>
              </label>

              <input
                v-model="form.role_title"
                type="text"
                placeholder="Enter role title"
              />
            </div>

            <!-- Meta Title -->
            <div class="form-group full">
              <label>
                Meta Title
              </label>

              <input
                v-model="form.role_meta_title"
                type="text"
                placeholder="Enter meta title"
              />
            </div>

            <!-- Keywords -->
            <div class="form-group full">
              <label>
                Keywords
              </label>

              <textarea
                v-model="form.role_keyword"
                rows="4"
                placeholder="Enter role keywords"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="secondary-btn"
            @click="closeModal"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            @click="saveRole"
          >
            <i class="bx bx-save"></i>

            {{ isEditing ? "Update Role" : "Save Role" }}
          </button>
        </div>
      </div>
    </div>

    <!-- View Modal -->
    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="showViewModal = false"
    >
      <div class="modal view-modal">
        <div class="modal-header">
          <div>
            <h2>Role Details</h2>
            <p>View role information.</p>
          </div>

          <button
            class="close-btn"
            @click="showViewModal = false"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div
          class="modal-body"
          v-if="selectedRole"
        >
          <div class="role-detail-header">
            <div class="large-role-icon">
              <i class="bx bx-shield"></i>
            </div>

            <div>
              <h3>
                {{ selectedRole.role_title }}
              </h3>

              <span>
                Role #{{ selectedRole.role_id }}
              </span>
            </div>
          </div>

          <div class="detail-grid">
            <div class="detail-item">
              <label>Role ID</label>

              <span>
                #{{ selectedRole.role_id }}
              </span>
            </div>

            <div class="detail-item">
              <label>Created</label>

              <span>
                {{ formatDate(selectedRole.role_datetime) }}
              </span>
            </div>

            <div class="detail-item full">
              <label>Role Title</label>

              <p>
                {{ selectedRole.role_title }}
              </p>
            </div>

            <div class="detail-item full">
              <label>Meta Title</label>

              <p>
                {{ selectedRole.role_meta_title || "—" }}
              </p>
            </div>

            <div class="detail-item full">
              <label>Keywords</label>

              <div class="keywords-box">
                {{ selectedRole.role_keyword || "—" }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="secondary-btn"
            @click="showViewModal = false"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="editRole(selectedRole)"
          >
            <i class="bx bx-edit"></i>
            Edit Role
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="showDeleteModal = false"
    >
      <div class="delete-modal">
        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h2>Delete Role?</h2>

        <p>
          Are you sure you want to delete
          <strong>
            {{ selectedRole?.role_title }}
          </strong>?
          This action cannot be undone.
        </p>

        <div class="delete-actions">
          <button
            class="secondary-btn"
            @click="showDeleteModal = false"
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            @click="confirmDelete"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  getRolesApi,
  getRoleStatsApi,
  getRoleApi,
  createRoleApi,
  updateRoleApi,
  deleteRoleApi,
} from "@/apis/roleapis";

export default {
  name: "RolesScreen",

  data() {
    return {
      search: "",

      currentPage: 1,
      perPage: 8,

      totalRoles: 0,
      configuredRoles: 0,
      roleTypes: 0,
      thisMonthCount: 0,

      roles: [],

      loading: false,
      saving: false,

      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,

      selectedRole: null,

      form: {
        role_id: null,
        role_title: "",
        role_meta_title: "",
        role_keyword: "",
      },
    };
  },

  computed: {
    totalPages() {
      return Math.max(
        Math.ceil(
          this.totalRoles /
            this.perPage
        ),
        1
      );
    },

    startItem() {
      if (!this.roles.length) {
        return 0;
      }

      return (
        (this.currentPage - 1) *
          this.perPage +
        1
      );
    },

    endItem() {
      if (!this.roles.length) {
        return 0;
      }

      return Math.min(
        this.currentPage *
          this.perPage,
        this.totalRoles
      );
    },

    paginatedRoles() {
      return this.roles;
    },

    filteredRoles() {
      return this.roles;
    },
  },

  watch: {
    search() {
      this.currentPage = 1;
      this.loadRoles();
    },
  },

  mounted() {
    this.loadStats();
    this.loadRoles();
  },

  methods: {
    emptyForm() {
      return {
        role_id: null,
        role_title: "",
        role_meta_title: "",
        role_keyword: "",
      };
    },

    async loadRoles() {
      this.loading = true;

      try {
        const res =
          await getRolesApi({
            page: this.currentPage,
            limit: this.perPage,
            search: this.search,
          });

        if (
          res?.status ||
          res?.success
        ) {
          const data =
            res.data || res;

          this.roles =
            data.roles || [];

          this.totalRoles =
            Number(
              data.total ??
                this.roles.length
            );

          if (data.totalPages) {
            this.totalPagesValue =
              Number(
                data.totalPages
              );
          }
        } else {
          this.roles = [];
          this.totalRoles = 0;
        }
      } catch (error) {
        console.error(
          "Roles list error:",
          error
        );

        this.roles = [];
        this.totalRoles = 0;
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        const res =
          await getRoleStatsApi();

        if (
          res?.status ||
          res?.success
        ) {
          const data =
            res.data || res;

          this.totalRoles =
            Number(
              data.totalRoles ??
                this.totalRoles ??
                0
            );

          this.configuredRoles =
            Number(
              data.configuredRoles ??
                0
            );

          this.roleTypes =
            Number(
              data.roleTypes ??
                data.totalRoles ??
                0
            );

          this.thisMonthCount =
            Number(
              data.thisMonth ??
                data.thisMonthCount ??
                0
            );
        }
      } catch (error) {
        console.error(
          "Role stats error:",
          error
        );
      }
    },

    async openAddModal() {
      this.isEditing = false;
      this.selectedRole = null;
      this.form = this.emptyForm();

      this.showFormModal = true;
    },

    async editRole(role) {
      if (!role?.role_id) {
        return;
      }

      this.saving = false;

      try {
        const res =
          await getRoleApi(
            role.role_id
          );

        if (
          res?.status ||
          res?.success
        ) {
          const data =
            res.data || res;

          this.form = {
            role_id:
              data.role_id ??
              role.role_id,

            role_title:
              data.role_title ??
              "",

            role_meta_title:
              data.role_meta_title ??
              "",

            role_keyword:
              data.role_keyword ??
              "",
          };
        } else {
          this.form = {
            role_id:
              role.role_id,

            role_title:
              role.role_title ||
              "",

            role_meta_title:
              role.role_meta_title ||
              "",

            role_keyword:
              role.role_keyword ||
              "",
          };
        }
      } catch (error) {
        console.error(
          "Role view error:",
          error
        );

        this.form = {
          role_id:
            role.role_id,

          role_title:
            role.role_title ||
            "",

          role_meta_title:
            role.role_meta_title ||
            "",

          role_keyword:
            role.role_keyword ||
            "",
        };
      }

      this.selectedRole = role;

      this.isEditing = true;

      this.showViewModal = false;
      this.showFormModal = true;
    },

    async saveRole() {
      if (
        !this.form.role_title ||
        !this.form.role_title.trim()
      ) {
        alert(
          "Role title is required."
        );

        return;
      }

      this.saving = true;

      try {
        let res;

        if (this.isEditing) {
          res =
            await updateRoleApi(
              this.form
            );
        } else {
          res =
            await createRoleApi(
              this.form
            );
        }

        if (
          res?.status ||
          res?.success
        ) {
          this.closeModal();

          await this.loadRoles();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Unable to save role."
          );
        }
      } catch (error) {
        console.error(
          "Role save error:",
          error
        );

        alert(
          "Unable to save role."
        );
      } finally {
        this.saving = false;
      }
    },

    async viewRole(role) {
      if (!role?.role_id) {
        return;
      }

      try {
        const res =
          await getRoleApi(
            role.role_id
          );

        if (
          res?.status ||
          res?.success
        ) {
          this.selectedRole =
            res.data || res;
        } else {
          this.selectedRole =
            role;
        }
      } catch (error) {
        console.error(
          "Role view error:",
          error
        );

        this.selectedRole =
          role;
      }

      this.showViewModal = true;
    },

    deleteRole(role) {
      this.selectedRole = role;
      this.showDeleteModal = true;
    },

    async confirmDelete() {
      if (
        !this.selectedRole?.role_id
      ) {
        return;
      }

      try {
        const res =
          await deleteRoleApi(
            this.selectedRole.role_id
          );

        if (
          res?.status ||
          res?.success
        ) {
          this.showDeleteModal =
            false;

          this.selectedRole =
            null;

          if (
            this.roles.length === 1 &&
            this.currentPage > 1
          ) {
            this.currentPage--;
          }

          await this.loadRoles();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Unable to delete role."
          );
        }
      } catch (error) {
        console.error(
          "Role delete error:",
          error
        );

        alert(
          "Unable to delete role."
        );
      }
    },

    closeModal() {
      this.showFormModal = false;
      this.isEditing = false;
      this.selectedRole = null;
      this.form = this.emptyForm();
    },

    resetFilters() {
      this.search = "";
      this.currentPage = 1;

      this.loadRoles();
    },

    async changePage(page) {
      if (
        page < 1 ||
        page > this.totalPages
      ) {
        return;
      }

      this.currentPage = page;

      await this.loadRoles();
    },

    formatDate(date) {
      if (!date) {
        return "—";
      }

      const parsed =
        new Date(date);

      if (
        Number.isNaN(
          parsed.getTime()
        )
      ) {
        return date;
      }

      return parsed.toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    },
  },

  created() {
    this.form = this.emptyForm();
  },
};
</script>
<style scoped>
.roles-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.reset-btn,
.danger-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s ease;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn,
.reset-btn {
  background: #fff;
  border-color: #e2e8f0;
  color: #475569;
}

.secondary-btn:hover,
.reset-btn:hover {
  background: #f8fafc;
}

.danger-btn {
  background: #dc2626;
  color: #fff;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-card span {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 4px;
}

.stat-card strong {
  display: block;
  font-size: 22px;
  line-height: 1;
}

/* Card */
.content-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Toolbar */
.toolbar {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 380px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input {
  width: 100%;
  height: 40px;
  padding: 0 12px 0 40px;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  outline: none;
  font-size: 13px;
  color: #334155;
}

.search-box input:focus,
.form-group input:focus,
.form-group textarea:focus {
  border-color: #2563eb;
}

/* Table */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 850px;
  border-collapse: collapse;
}

thead {
  background: #f8fafc;
}

th {
  padding: 13px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  vertical-align: middle;
}

tbody tr:hover {
  background: #fafcff;
}

.action-column {
  width: 125px;
  text-align: right;
}

/* Role */
.role-info {
  display: flex;
  align-items: center;
  gap: 11px;
}

.role-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
}

.role-info strong {
  display: block;
  color: #1e293b;
  font-size: 13px;
}

.role-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.meta-cell,
.keyword-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #64748b;
}

.date-text {
  white-space: nowrap;
  color: #64748b;
}

/* Actions */
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.icon-btn.view {
  color: #2563eb;
}

.icon-btn.edit {
  color: #64748b;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn:hover {
  background: #f8fafc;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h3 {
  margin: 12px 0 5px;
  font-size: 16px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  padding: 14px 16px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.pagination-info {
  color: #64748b;
  font-size: 12px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 7px;
  color: #475569;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.pagination-buttons button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
}

.view-modal {
  max-width: 700px;
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.modal-header h2 {
  margin: 0 0 5px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  background: #f8fafc;
  border-radius: 7px;
  color: #64748b;
  cursor: pointer;
  font-size: 20px;
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 13px;
  color: #334155;
  outline: none;
}

.form-group input {
  height: 40px;
}

.form-group textarea {
  resize: vertical;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* View */
.role-detail-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.large-role-icon {
  width: 56px;
  height: 56px;
  border-radius: 11px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
}

.role-detail-header h3 {
  margin: 0 0 5px;
  font-size: 18px;
}

.role-detail-header span {
  color: #94a3b8;
  font-size: 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item.full {
  grid-column: 1 / -1;
}

.detail-item label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.detail-item span,
.detail-item p {
  margin: 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.keywords-box {
  padding: 13px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px;
  color: #475569;
  line-height: 1.6;
  font-size: 13px;
}

/* Delete */
.delete-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
}

.delete-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 23px;
}

.delete-modal h2 {
  margin: 0 0 8px;
  font-size: 18px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.delete-actions {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

/* Responsive */
@media (max-width: 1000px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .roles-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .form-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full,
  .detail-item.full {
    grid-column: auto;
  }

  .pagination {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
